package com.macrofocus.eflux.ui.svg

import hammer.Hammer
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import org.w3c.xhr.XMLHttpRequest
import react.*
import react.dom.events.MouseEvent
import react.dom.html.ReactHTML.div
import react.dom.svg.ReactSVG.svg
import svgpanzoom.DefaultOptions
import svgpanzoom.SvgPanZoomContext
import svgpanzoom.svgPanZoom
import web.cssom.*
import web.html.HTMLDivElement
import web.html.HtmlSource
import web.resize.ResizeObserver
import web.svg.SVGElement
import web.svg.SVGSVGElement

external interface ResizableSVGProps : PropsWithChildren {
    var background: String?
    var url: String
    var svgElementChanged: (SVGSVGElement?, SvgPanZoomContext?) -> Unit
    var onZoom: (ctm: Any) -> Unit
    var onUpdate: (ctm: Any) -> Unit
    var divRef: RefObject<HTMLDivElement>
    var svgRef: RefObject<SVGElement>

    var onMouseDownFunction: (event: MouseEvent<*, *>) -> Unit
    var onMouseUpFunction: (event: MouseEvent<*, *>) -> Unit
    var onMouseMoveFunction: (event: MouseEvent<*, *>) -> Unit
    var onMouseEnterFunction: (event: MouseEvent<*, *>) -> Unit
    var onMouseOutFunction: (event: MouseEvent<*, *>) -> Unit
}

val ResizableSVG = FC<ResizableSVGProps> { props ->
    val HAMMER = true
    val DEBUG = false

    val (width, setWidth) = useState { 1600 }
    val (height, setHeight) = useState { 1200 }

    if (DEBUG) println("Creating resizable SVG ($width, $height)")

    useEffectOnce {
        val div = props.divRef.current!!
        val resizeObserver = ResizeObserver { _, _ ->
            resize(div, setWidth, setHeight)
        }
        resizeObserver.observe(div)

        awaitCancellation {
            resizeObserver.unobserve(div)
            resizeObserver.disconnect()
        }
    }

    // Load the SVG graphic given a URL
    useEffectOnce {
        run {
            if (DEBUG) println("Loading ${props.url}")
            val request = XMLHttpRequest()
            request.open("GET", props.url)
            request.onload = {
                if (request.readyState == 4.toShort() && request.status == 200.toShort()) {
                    println("Diagram ${props.url} loaded")
                    // Attach the SVG graphics to the SVG DOM element
                    val current = props.svgRef.current!! as SVGSVGElement
                    current.innerHTML = HtmlSource(request.responseText)
                    println("Diagram ${props.url} parsed")

                    val svgPanZoomContext: SvgPanZoomContext?
                    val options = DefaultOptions()

                    if (HAMMER) {
                        val eventsHandler = unsafeJso<dynamic> {
                            zoomEnabled = true
                            haltEventListeners = arrayOf("touchstart", "touchend", "touchmove", "touchleave", "touchcancel")
                            init = { options: dynamic ->
                                val instance = options.instance
                                var initialScale = 1
                                var pannedX = 0.0
                                var pannedY = 0.0

                                // Init Hammer
                                // Listen only for pointer and touch events
                                val hammer = Hammer(options.svgElement, unsafeJso<dynamic> {
                                    inputClass =
                                        if (js("Hammer.SUPPORT_POINTER_EVENTS"))
                                            js("Hammer.PointerEventInput")
                                        else
                                            js("Hammer.TouchInput")
                                })

                                // Enable pinch
                                hammer.get("pinch").set(unsafeJso<dynamic> {
                                    enable = true
                                })

                                // Handle double tap - can freeze (see DIVISO-T-175)
//                                hammer.on("doubletap") {
//                                    if (DEBUG) println("hammer doubletap")
//                                    instance.zoomIn()
//                                }

                                // Handle pan
                                hammer.on("panstart panmove") { ev ->
                                    if (DEBUG) println("hammer ${ev.type} ${ev.srcEvent}")
                                    // On pan start reset panned variables
                                    if (ev.type === "panstart") {
                                        pannedX = 0.0
                                        pannedY = 0.0
                                    }

                                    // Pan only the difference
                                    try {
                                        val point = unsafeJso<dynamic> {
                                            x = ev.deltaX - pannedX
                                            y = ev.deltaY - pannedY
                                        }
                                        if (DEBUG) println("hammer $instance panBy(${point.x},${point.y})")
                                        instance.panBy(point)
//                                        instance.panBy(unsafeJso {
//                                            val x = ev.deltaX - pannedX
//                                            val y = ev.deltaY - pannedY
//                                        })
                                        pannedX = ev.deltaX
                                        pannedY = ev.deltaY
                                    } catch (e: Error) {
                                        println("Error while panning: $e")
//                                        pannedX = 0.0
//                                        pannedY = 0.0
                                    }
                                }

                                // Handle pinch
                                hammer.on("pinchstart pinchmove") { ev ->
                                    if (DEBUG) println("hammer ${ev.type}")
                                    // On pinch start remember initial zoom
                                    if (ev.type === "pinchstart") {
                                        initialScale = instance.getZoom()
                                        val point = unsafeJso<dynamic> {
                                            x = ev.center.x
                                            y = ev.center.y
                                        }
                                        instance.zoomAtPoint(initialScale * ev.scale, point)
                                    }

                                    val point = unsafeJso<dynamic> {
                                        x = ev.center.x
                                        y = ev.center.y
                                    }
                                    instance.zoomAtPoint(initialScale * ev.scale, point)
                                }

                                // Prevent moving the page on some devices when panning over SVG
                                options.svgElement.addEventListener("touchmove") { e ->
                                    if (DEBUG) println("hammer touchmove")
                                    e.preventDefault()
                                }
                            }
                        }
                        options.customEventsHandler = eventsHandler
                    }

                    // Register pan and zoom handler
                    options.minZoom = 1.0
                    options.maxZoom = 80.0
                    options.dblClickZoomEnabled = false
                    options.onZoom = props.onZoom
//                    options.onPan = props.onPan
                    options.onUpdatedCTM = props.onUpdate
                    options.eventsListenerElement = props.svgRef.current
//                    options.beforePan = { ->
//                        if (DEBUG) println("beforePan")
//                    }
                    svgPanZoomContext = svgPanZoom(current, options)
                    if (DEBUG) println("${svgPanZoomContext.isDblClickZoomEnabled()}")

                    // Notify that the SVG element has been loaded
                    if (DEBUG) println("svgElementChanged($current, $svgPanZoomContext)")
                    props.svgElementChanged(current, svgPanZoomContext)
                } else {
                    if (DEBUG) println("svgElementChanged(null, null)")
                    props.svgElementChanged(null, null)
                }
            }
            request.send()
        }
    }

    div {
        style = unsafeJso {
            position = Position.absolute
            left = 0.px
            right = 0.px
            top = 0.px
            bottom = 0.px
            background = Color(props.background ?: "#062333")
            pointerEvents = None.none
        }
//        attrs {
//            onMouseDownFunction = props.onmousedown
//            onMouseMoveFunction = props.onmousemove
        // ToDo: why doesn't this exist?
//                onMouseEnterFunction = props.onmouseenter
//            onMouseOutFunction = props.onmouseout
//        onDragEnter = {
//            it.preventDefault()
//        }
//        onDragOver = {
//            it.preventDefault()
//        }
//        }
        ref = props.divRef

        svg {
            style = unsafeJso {
                pointerEvents = PointerEvents.all
                transform = "/* translatey(-16px) perspective(1254px) rotateX(50deg) scale(0.9); */".unsafeCast<Transform>()
                overflow = Overflow.visible // So that perspective effects do not get clipped
//                transformStyle = TransformStyle.preserve3d
                this.width = 100.pct
                this.height = 100.pct
            }

            this.width = width.toDouble()
            this.height = height.toDouble()
//                        set("viewBox", "0 0 1600 1200")

            onMouseDown = props.onMouseDownFunction
            onMouseUp = props.onMouseUpFunction
            onMouseMove = props.onMouseMoveFunction
            // ToDo: why doesn't this exist?
            onMouseEnter = props.onMouseEnterFunction
            onMouseOut = props.onMouseOutFunction

            ref = props.svgRef
        }

        +props.children
    }
}


